const normalizeThreshold = (threshold, index) => {
    const minQuantity = Number(threshold?.minQuantity ?? 0);
    const discountPercent = Number(threshold?.discountPercent ?? 0);

    return {
        id: threshold?.id ?? `bundle-threshold-${index}`,
        title: threshold?.title ?? '',
        label: threshold?.label ?? '',
        minQuantity: Number.isFinite(minQuantity) ? minQuantity : 0,
        discountPercent: Number.isFinite(discountPercent) ? discountPercent : 0,
    };
};

const sortThresholds = (thresholds) => {
    return [...thresholds].sort((a, b) => {
        if (a.minQuantity === b.minQuantity) {
            return a.discountPercent - b.discountPercent;
        }

        return a.minQuantity - b.minQuantity;
    });
};

export const registerBundleThresholdsStore = (Alpine) => {
    const rawThresholds = Array.isArray(window.__PUREMETICS_BUNDLE_THRESHOLDS__)
        ? window.__PUREMETICS_BUNDLE_THRESHOLDS__
        : [];

    const thresholds = sortThresholds(
        rawThresholds
            .map(normalizeThreshold)
            .filter((threshold) => threshold.minQuantity > 0)
    );

    Alpine.store('bundleThresholds', {
        thresholds,

        get hasThresholds() {
            return this.thresholds.length > 0;
        },

        getThresholds() {
            return this.thresholds;
        },

        getBaseThreshold() {
            return {
                id: 'bundle-threshold-base',
                title: '',
                label: '1 unit',
                minQuantity: 1,
                discountPercent: 0,
            };
        },

        getApplicableOrBaseThreshold(quantity) {
            return this.getApplicableThreshold(quantity) || this.getBaseThreshold();
        },

        getApplicableThreshold(quantity) {
            const parsedQuantity = Number(quantity) || 0;
            let activeThreshold = null;

            this.thresholds.forEach((threshold) => {
                if (parsedQuantity >= threshold.minQuantity) {
                    activeThreshold = threshold;
                }
            });

            return activeThreshold;
        },

        getNextThreshold(quantity) {
            const parsedQuantity = Number(quantity) || 0;

            return this.thresholds.find((threshold) => parsedQuantity < threshold.minQuantity) || null;
        },

        getDiscountPercent(quantity) {
            return this.getApplicableThreshold(quantity)?.discountPercent || 0;
        },

        getDiscountMultiplier(quantity) {
            return 1 - (this.getDiscountPercent(quantity) / 100);
        },

        getDiscountedUnitPrice(basePrice, quantity) {
            const parsedPrice = Number(basePrice) || 0;
            return Math.round(parsedPrice * this.getDiscountMultiplier(quantity));
        },

        getTotalPrice(basePrice, quantity) {
            const parsedQuantity = Math.max(1, Number(quantity) || 1);
            return this.getDiscountedUnitPrice(basePrice, parsedQuantity) * parsedQuantity;
        },

        getSavingsAmount(basePrice, quantity) {
            const parsedPrice = Number(basePrice) || 0;
            const parsedQuantity = Math.max(1, Number(quantity) || 1);
            const compareTotal = parsedPrice * parsedQuantity;
            const discountedTotal = this.getTotalPrice(parsedPrice, parsedQuantity);

            return Math.max(0, compareTotal - discountedTotal);
        },

        getOfferOptions(basePrice) {
            const parsedPrice = Number(basePrice) || 0;
            const baseThreshold = this.getBaseThreshold();
            const allThresholds = [baseThreshold, ...this.thresholds];

            return allThresholds.map((threshold) => {
                const unitPrice = Math.round(parsedPrice * (1 - (threshold.discountPercent / 100)));
                const quantity = Math.max(1, threshold.minQuantity);

                return {
                    ...threshold,
                    quantity,
                    unitPrice,
                    totalPrice: unitPrice * quantity,
                };
            });
        },

        formatMoney(price) {
            return new Intl.NumberFormat('de-DE', {
                style: 'currency',
                currency: 'EUR',
            }).format((Number(price) || 0) / 100);
        },

        getProgressToNextThreshold(quantity) {
            const parsedQuantity = Number(quantity) || 0;
            const nextThreshold = this.getNextThreshold(parsedQuantity);
            const activeThreshold = this.getApplicableThreshold(parsedQuantity);
            const currentFloor = activeThreshold?.minQuantity || 0;

            if (!nextThreshold) {
                return 1;
            }

            const range = nextThreshold.minQuantity - currentFloor;

            if (range <= 0) {
                return 0;
            }

            return Math.min(1, Math.max(0, (parsedQuantity - currentFloor) / range));
        },
    });
};
